import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  MapPin, Phone, Mail, Clock, MessageCircle, Send,
} from 'lucide-react';

const contactInfo = [
  {
    icon: MapPin,
    label: 'Alamat',
    value: 'Jl. Ahmad Yani No. 123, Pontianak Kota, Kalimantan Barat 78112',
  },
  {
    icon: Phone,
    label: 'Telepon / WhatsApp',
    value: '+62 812-3456-7890',
    href:  'https://wa.me/6281234567890',
  },
  {
    icon: Mail,
    label: 'Email',
    value: 'info@kantorhukumpontianak.com',
    href:  'mailto:info@kantorhukumpontianak.com',
  },
  {
    icon: Clock,
    label: 'Jam Operasional',
    value: 'Senin – Jumat: 08.00 – 17.00 WIB\nSabtu: 08.00 – 13.00 WIB',
  },
];

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [form, setForm] = useState({ name: '', phone: '', service: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = encodeURIComponent(
      `Halo, saya ${form.name}.\nNo HP: ${form.phone}\nLayanan: ${form.service}\n\n${form.message}`
    );
    window.open(`https://wa.me/6281234567890?text=${msg}`, '_blank');
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <section id="kontak" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-600/30 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-gold-400 text-sm font-semibold tracking-widest uppercase mb-3">
            Hubungi Kami
          </span>
          <h2 className="section-title mb-4">
            Mari{' '}
            <span className="gold-text">Berdiskusi</span>
          </h2>
          <div className="divider-gold mx-auto mb-5" />
          <p className="section-subtitle max-w-2xl mx-auto">
            Kami siap membantu Anda. Hubungi kami sekarang untuk mendapatkan
            pendampingan hukum yang Anda butuhkan.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Contact info + Map */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="space-y-6"
          >
            {contactInfo.map((item, i) => (
              <div
                key={item.label}
                className="glass-light rounded-2xl p-5 flex gap-4 border border-white/5 hover:border-gold-600/20 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center flex-shrink-0 shadow-md">
                  <item.icon size={19} className="text-navy-950" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-navy-400 text-xs font-semibold uppercase tracking-wider mb-1">
                    {item.label}
                  </p>
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white text-sm font-medium hover:text-gold-400 transition-colors"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-white text-sm font-medium whitespace-pre-line">{item.value}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Map embed placeholder */}
            <div className="glass-light rounded-2xl overflow-hidden border border-white/5 h-56">
              <iframe
                title="Lokasi Kantor Hukum"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63871.29959793485!2d109.28734!3d-0.02329!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e1d57604d7a3edd%3A0x6daf31c2c6d6fd75!2sPontianak%2C%20Kalimantan%20Barat!5e0!3m2!1sid!2sid!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Right: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="glass rounded-2xl p-8 border border-white/5"
          >
            <h3 className="font-serif text-2xl font-bold text-white mb-2">
              Kirim Pesan
            </h3>
            <p className="text-navy-400 text-sm mb-7">
              Isi formulir di bawah dan kami akan menghubungi Anda melalui WhatsApp.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-navy-300 text-xs font-semibold uppercase tracking-wider mb-2">
                    Nama Lengkap *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Nama Anda"
                    className="w-full glass-light rounded-xl px-4 py-3 text-sm text-white placeholder-navy-500 border border-white/10 focus:border-gold-500/50 focus:outline-none focus:ring-1 focus:ring-gold-500/30 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-navy-300 text-xs font-semibold uppercase tracking-wider mb-2">
                    No. WhatsApp *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="08xx-xxxx-xxxx"
                    className="w-full glass-light rounded-xl px-4 py-3 text-sm text-white placeholder-navy-500 border border-white/10 focus:border-gold-500/50 focus:outline-none focus:ring-1 focus:ring-gold-500/30 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-navy-300 text-xs font-semibold uppercase tracking-wider mb-2">
                  Jenis Layanan
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  className="w-full glass-light rounded-xl px-4 py-3 text-sm text-white border border-white/10 focus:border-gold-500/50 focus:outline-none focus:ring-1 focus:ring-gold-500/30 transition-all bg-transparent"
                >
                  <option value="" className="bg-navy-900">Pilih layanan...</option>
                  <option value="Hukum Perusahaan" className="bg-navy-900">Hukum Perusahaan</option>
                  <option value="Sengketa Tanah" className="bg-navy-900">Sengketa Tanah & Properti</option>
                  <option value="Perjanjian & Kontrak" className="bg-navy-900">Perjanjian & Kontrak</option>
                  <option value="Litigasi" className="bg-navy-900">Litigasi & Pengadilan</option>
                  <option value="Konsultasi Hukum" className="bg-navy-900">Konsultasi Hukum</option>
                  <option value="Hukum Keluarga" className="bg-navy-900">Hukum Keluarga</option>
                </select>
              </div>

              <div>
                <label className="block text-navy-300 text-xs font-semibold uppercase tracking-wider mb-2">
                  Pesan / Deskripsi Masalah *
                </label>
                <textarea
                  name="message"
                  required
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Ceritakan permasalahan hukum Anda secara singkat..."
                  className="w-full glass-light rounded-xl px-4 py-3 text-sm text-white placeholder-navy-500 border border-white/10 focus:border-gold-500/50 focus:outline-none focus:ring-1 focus:ring-gold-500/30 transition-all resize-none"
                />
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-primary w-full justify-center text-base"
              >
                {sent ? (
                  <>Pesan Terkirim! ✓</>
                ) : (
                  <>
                    <MessageCircle size={18} />
                    Kirim via WhatsApp
                    <Send size={16} />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
